import _ from 'lodash';

import store from '@/store';

export default class WebSocketService {
  constructor(options) {
    this.options = _.defaults(options, {
      baseURL: `${process.env.VUE_APP_WS_BASE_URL}`,
      reconnectInterval: 1000,
    });
    this.reconnectInterval = this.options.reconnectInterval;
    this.eventHandlers = {};
    this.reconnectTimer = null;

    this.onOpen = this.onOpen.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onError = this.onError.bind(this);
  }

  /**
   * Register consumer event handlers (e.g. `message`) once; they are re-attached
   * to every new underlying WebSocket created by connect()/reconnect().
   */
  setEventHandlers(handlers) {
    this.eventHandlers = handlers || {};
  }

  connect() {
    if (this.ws) {
      this.disconnect();
    }

    // We pass the access token in the Sec-WebSocket-Protocol header
    // This is used by the back-end to authenticate the request
    this.ws = new WebSocket(
      this.options.baseURL + this.options.namespace,
      store.state.auth.credentials.access_token
    );
    this.ws.addEventListener('open', this.onOpen);
    this.ws.addEventListener('close', this.onClose);
    this.ws.addEventListener('error', this.onError);
    Object.keys(this.eventHandlers).forEach(key => {
      this.ws.addEventListener(key, this.eventHandlers[key]);
    });
  }

  onOpen() {
    // Reset reconnection interval
    this.reconnectInterval = this.options.reconnectInterval;
  }

  onClose(event) {
    // Event.code 1000 is our normal close event
    if (event && event.code !== 1000) {
      this.reconnect();
    }
  }

  onError(error) {
    /* eslint-disable-next-line no-console */
    console.error(error);
    this.disconnect();
    this.reconnect();
  }

  disconnect() {
    // A pending reconnect would otherwise resurrect the socket after teardown
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // onError() already calls disconnect() before scheduling a reconnect, so this can be
    // called again (e.g. from component teardown) while `ws` is already null.
    if (!this.ws) return;

    this.ws.close();
    this.ws.removeEventListener('open', this.onOpen);
    this.ws.removeEventListener('close', this.onClose);
    this.ws.removeEventListener('error', this.onError);
    Object.keys(this.eventHandlers).forEach(key => {
      this.ws.removeEventListener(key, this.eventHandlers[key]);
    });
    this.ws = null;
  }

  send(data) {
    if (this.ws.readyState === 1) {
      this.ws.send(JSON.stringify(data));
    } else {
      /* eslint-disable-next-line no-console */
      console.warn('Can’t send message, WebSocket is not open.');
    }
  }

  reconnect() {
    let maxReconnectInterval = this.options.maxReconnectInterval || 3000;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.reconnectInterval < maxReconnectInterval) {
        this.reconnectInterval += 1000;
      }
      this.connect();
    }, this.reconnectInterval);
  }
}
